import { isMetricKey, MetricKey, shownKeys } from '../../common/metrics';
import {
  HeartRate,
  SupportAccessRight,
  TOSData,
  TrendsQuery,
  UserData,
} from '../../common/types';
import { isDefined } from '../../common/utils';
import {
  defaultTrendsConfigurationName,
  defaultTrendsQuery,
} from '../defaults';
import {
  ApiHeartRate,
  ApiSupportAccessRight,
  GetTOSResponse,
  GetUserInfoResponse,
  TrendsSettingsPayload,
  TrendsSettingsResponse,
} from '../types';
import { parseTimestamp } from './time';

export function toTOS({
  terms_of_service_version,
  terms_of_service_url,
  privacy_policy_version,
  privacy_policy_url,
  accepted,
}: GetTOSResponse): TOSData {
  return {
    termsOfServiceVersion: terms_of_service_version,
    termsOfServiceUrl: terms_of_service_url,
    privacyPolicyVersion: privacy_policy_version,
    privacyPolicyUrl: privacy_policy_url,
    accepted,
  };
}

export function toUserInfo(data: GetUserInfoResponse): UserData {
  const {
    email,
    analytics_uid: analyticsUID,
    user_uid: uid,
    user_settings: userInfo,
  } = data;

  return {
    analyticsUID,
    uid,
    email,
    userInfo: {
      gender: userInfo.gender ?? 'other',
      weight: userInfo.weight ?? undefined,
      height: userInfo.height ?? undefined,
      age: userInfo.age ?? undefined,
      units: userInfo.units ?? undefined,
    },
    isHipaa: data.hipaa?.enabled,
  };
}

// If a user has metrics that are no longer allowed to be selected, drop them
function isValidQueryMetric(key: unknown): key is MetricKey {
  return isMetricKey(key) && shownKeys.includes(key);
}

export function toQueryState(
  trendsSettings: TrendsSettingsResponse,
): TrendsQuery {
  // Note: even though the data structure supports multiple named configurations,
  // we only use the first one for now.
  const settings = trendsSettings.configurations[0];
  if (!settings) {
    // This really shouldn't happen, but occurs in our integration tests.
    console.error(
      `No settings found in response (using defaults): ${JSON.stringify(
        trendsSettings,
      )}`,
    );

    return {
      ...defaultTrendsQuery,
    };
  }

  return {
    timeAggregation: settings.time_aggregation,
    subqueries: settings.charts
      .map((c) =>
        // If a user has metrics in the query that are no longer allowed to be selected,
        // drop the query
        isValidQueryMetric(c.left_y) &&
        (c.right_y == null || isValidQueryMetric(c.right_y))
          ? {
              key: c.left_y,
              y1: c.left_y,
              y2: c.right_y,
            }
          : undefined,
      )
      .filter(isDefined),
    setLocally: false,
  };
}

export function toTrendsSettings(
  queryState: TrendsQuery,
): TrendsSettingsPayload {
  return {
    settings_type: 'trends',
    version: 1,
    configurations: [
      {
        name: defaultTrendsConfigurationName,
        time_aggregation: queryState.timeAggregation,
        charts: queryState.subqueries.map((q) => ({
          type: 'trend',
          left_y: q.y1,
          right_y: q.y2,
        })),
      },
    ],
  };
}

export function toSupportAccessRight({
  uid,
  requester_email,
  state,
  expires_at,
}: ApiSupportAccessRight): SupportAccessRight {
  return {
    id: uid,
    requesterEmail: requester_email,
    state,
    expiresAt: parseTimestamp(expires_at),
  };
}

export function toHeartRate(d: ApiHeartRate): HeartRate {
  return {
    bpm: d.bpm,
    source: d.source,
    quality: d.quality,
    restorative: d.restorative,
    timestamp: parseTimestamp(d.timestamp),
  };
}
